import { useCallback, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import type { Document } from "@/types/documents";
import {
  getTrashedDocuments,
  moveToTrash,
  restoreDocument,
  permanentlyDeleteDocument,
  emptyTrash,
} from "../services/documentService";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { logger } from "@/utils/logger";
import {
  notifyIfPinned,
  withoutPinnedDocuments,
} from "@/domains/documents/services/pinnedDocuments";
import {
  BuildingStorageQuotaError,
  storageAdmissionErrorFrom,
} from "../services/buildingStorageQuota";

function expectedStorageError(error: unknown): BuildingStorageQuotaError | null {
  if (error instanceof BuildingStorageQuotaError) return error;
  return error && typeof error === "object"
    ? storageAdmissionErrorFrom(error as { code?: string; message?: string })
    : null;
}

async function restoreSelectedDocuments(documents: Document[], buildingId: string) {
  let restored = 0;
  let quotaFailure: BuildingStorageQuotaError | null = null;
  for (const document of documents) {
    try {
      const success = await restoreDocument(document);
      if (success) restored += 1;
    } catch (error) {
      const expectedError = expectedStorageError(error);
      if (expectedError) quotaFailure = expectedError;
      else {
        void logger.error(
          "ui",
          "documents.bulk-restore",
          "Selected document restore failed",
          error instanceof Error ? error : new Error(String(error)),
          { buildingId }
        );
      }
    }
  }
  return { restored, quotaFailure };
}

interface UseTrashOperationsProps {
  buildingId: string;
  /** When set, the trash list is scoped to this project (`building_projects.id`). */
  projectId?: string | null;
  onRefresh?: () => Promise<void>;
}

export function useTrashOperations({ buildingId, projectId, onRefresh }: UseTrashOperationsProps) {
  const { toast } = useToast();
  const { userProfile } = useAuth();
  const queryClient = useQueryClient();

  /**
   * Invalidate all document-related queries to ensure sync across views.
   * This is the key fix for the bug where deleting from project page
   * doesn't reflect on documents page and vice versa.
   */
  const invalidateDocumentQueries = useCallback(() => {
    // Invalidate main documents query (used by documents page)
    queryClient.invalidateQueries({ queryKey: ["documents", buildingId] });

    // Invalidate trashed documents query
    queryClient.invalidateQueries({ queryKey: ["trashed-documents", buildingId] });

    // Invalidate the folder tree so soft-delete and restore changes show up
    // (folder rows are kept; visibility is filtered by is_deleted).
    queryClient.invalidateQueries({ queryKey: ["document-folders", buildingId] });

    // Invalidate chatbot document picker queries so deleted docs
    // don't appear in the "Add file" picker (WHA-1871)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) && (key[0] === "chat-picker-docs" || key[0] === "chat-picker-folders")
        );
      },
    });
  }, [queryClient, buildingId]);

  const {
    data: trashedDocuments = [],
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["trashed-documents", buildingId, projectId ?? null],
    queryFn: () => getTrashedDocuments(buildingId, projectId),
    enabled: !!buildingId,
  });

  // Auto-purge any documents that exceeded retention when user opens trash
  useEffect(() => {
    (async () => {
      try {
        const module = await import("../services/documentService");
        const result = await module.purgeExpiredTrashedDocuments(buildingId);
        if (result.success && result.count > 0) {
          await refetch();
        }
      } catch (e) {
        // Best-effort purge; ignore errors
        console.warn("Trash auto-purge skipped:", e);
      }
    })();
  }, [buildingId, refetch]);

  const handleMoveToTrash = useCallback(
    async (document: Document) => {
      if (!userProfile?.id) {
        toast({
          variant: "destructive",
          title: "Authentication required",
          description: "You must be logged in to perform this action",
        });
        return false;
      }

      try {
        // An issue attachment says why in its own toast and stays.
        const [trashableId] = await withoutPinnedDocuments([document.id], "trash");
        if (!trashableId) return false;
        const success = await moveToTrash(document, userProfile.id);
        if (success) {
          toast({
            title: "Document moved to trash",
            description: `${document.filename} has been moved to trash`,
          });
          // Invalidate all document-related queries to sync across views
          invalidateDocumentQueries();
          // Refresh both the main documents list and the trash documents
          if (onRefresh) await onRefresh();
          await refetch(); // This refreshes the trash documents
          return true;
        }
        throw new Error("Failed to move document to trash");
      } catch (error) {
        // A pinned file gets its own toast naming it, not the generic failure.
        if (notifyIfPinned(error)) return false;
        console.error("Move to trash error:", error);
        toast({
          variant: "destructive",
          title: "Move to trash failed",
          description: "Could not move the document to trash",
        });
        return false;
      }
    },
    [userProfile?.id, toast, onRefresh, refetch, invalidateDocumentQueries]
  );

  const handleRestoreDocument = useCallback(
    async (document: Document) => {
      try {
        const success = await restoreDocument(document);
        if (!success) {
          throw new Error("Failed to restore document");
        }
        toast({
          title: "Document restored",
          description: `${document.filename} has been restored`,
        });
        // Invalidate all document-related queries to sync across views
        invalidateDocumentQueries();
        await refetch();
        if (onRefresh) await onRefresh();
        return true;
      } catch (error) {
        const quotaError = expectedStorageError(error);
        if (!quotaError) console.error("Restore error:", error);
        toast({
          variant: "destructive",
          title: "Restore failed",
          description: quotaError?.message ?? "Could not restore the document",
        });
        // Rethrow so callers (e.g., UI components) can react appropriately
        throw error;
      }
    },
    [toast, refetch, onRefresh, invalidateDocumentQueries]
  );

  const handlePermanentlyDelete = useCallback(
    async (document: Document) => {
      try {
        const success = await permanentlyDeleteDocument(document);
        if (success) {
          toast({
            title: "Document permanently deleted",
            description: `${document.filename} has been permanently deleted`,
          });
          // Invalidate all document-related queries to sync across views
          invalidateDocumentQueries();
          await refetch();
          return true;
        }
        throw new Error("Failed to permanently delete document");
      } catch (error) {
        console.error("Permanent delete error:", error);
        toast({
          variant: "destructive",
          title: "Permanent delete failed",
          description: "Could not permanently delete the document",
        });
        return false;
      }
    },
    [toast, refetch, invalidateDocumentQueries]
  );

  // Permanently delete a specific subset of trashed documents (the user's
  // current selection). Runs sequentially — permanentlyDeleteDocument removes
  // every version sibling of each document, so concurrent calls could race on
  // overlapping rows. Reports a single summary toast and refreshes once.
  const handleBulkPermanentlyDelete = useCallback(
    async (documents: Document[]) => {
      if (documents.length === 0) return false;
      let deleted = 0;
      try {
        for (const document of documents) {
          const success = await permanentlyDeleteDocument(document);
          if (success) deleted += 1;
        }
        if (deleted > 0) {
          toast({
            title: "Documents permanently deleted",
            description: `${deleted} document(s) have been permanently deleted`,
          });
          invalidateDocumentQueries();
          await refetch();
        }
        if (deleted < documents.length) {
          toast({
            variant: "destructive",
            title: "Permanent delete failed",
            description: `${documents.length - deleted} document(s) could not be permanently deleted`,
          });
          return false;
        }
        return true;
      } catch (error) {
        console.error("Bulk permanent delete error:", error);
        // Refresh anyway so any partial deletes are reflected in the list.
        invalidateDocumentQueries();
        await refetch();
        toast({
          variant: "destructive",
          title: "Permanent delete failed",
          description: "Could not permanently delete the selected documents",
        });
        return false;
      }
    },
    [toast, refetch, invalidateDocumentQueries]
  );

  // Restore a specific subset of trashed documents (the user's current
  // selection). Mirrors handleRestoreDocument but as a single batch with one
  // summary toast and one refresh.
  const handleBulkRestore = useCallback(
    async (documents: Document[]) => {
      if (documents.length === 0) return false;
      const { restored, quotaFailure } = await restoreSelectedDocuments(documents, buildingId);
      if (restored > 0) {
        toast({
          title: "Documents restored",
          description: `${restored} document(s) have been restored`,
        });
        invalidateDocumentQueries();
        await refetch();
        if (onRefresh) await onRefresh();
      }
      if (restored < documents.length) {
        toast({
          variant: "destructive",
          title: "Restore failed",
          description:
            quotaFailure?.message ??
            `${documents.length - restored} document(s) could not be restored`,
        });
        return false;
      }
      return true;
    },
    [toast, refetch, onRefresh, invalidateDocumentQueries, buildingId]
  );

  const handleEmptyTrash = useCallback(async () => {
    try {
      const result = await emptyTrash(buildingId);
      if (result.success) {
        toast({
          title: "Trash emptied",
          description: `${result.count} document(s) permanently deleted`,
        });
        // Invalidate all document-related queries to sync across views
        invalidateDocumentQueries();
        await refetch();
        return true;
      }
      throw new Error("Failed to empty trash");
    } catch (error) {
      console.error("Empty trash error:", error);
      toast({
        variant: "destructive",
        title: "Empty trash failed",
        description: "Could not empty the trash",
      });
      return false;
    }
  }, [buildingId, toast, refetch, invalidateDocumentQueries]);

  return {
    trashedDocuments,
    isLoading,
    handleMoveToTrash,
    handleRestoreDocument,
    handlePermanentlyDelete,
    handleBulkPermanentlyDelete,
    handleBulkRestore,
    handleEmptyTrash,
    refetch,
    // Exposed so bulk-delete and folder-delete callers (which write directly
    // via the MST store, not through this hook) can flush the trashed-
    // documents and folder-tree react-query caches.
    invalidateDocumentQueries,
  };
}
